import React, { useCallback, useEffect, useRef, useState } from 'react';
import './Skills.css';

// Hook: tracks the mouse position within an SVG element, mapped into the
// SVG's viewBox (0..320, 0..200). rAF-throttled and shared across all five
// case-study visuals so they all react to cursor movement consistently.
const VB_W = 320;
const VB_H = 200;
const useSvgMouse = () => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: VB_W / 2, y: VB_H / 2, active: false });
  const targetRef = useRef({ x: VB_W / 2, y: VB_H / 2, active: false });
  const rafRef = useRef(0);

  const flush = useCallback(() => {
    rafRef.current = 0;
    setPos(targetRef.current);
  }, []);

  const handleMove = useCallback(
    (e) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      targetRef.current = {
        x: ((e.clientX - rect.left) / rect.width) * VB_W,
        y: ((e.clientY - rect.top) / rect.height) * VB_H,
        active: true,
      };
      if (!rafRef.current) rafRef.current = requestAnimationFrame(flush);
    },
    [flush],
  );

  const handleLeave = useCallback(() => {
    targetRef.current = { ...targetRef.current, active: false };
    if (!rafRef.current) rafRef.current = requestAnimationFrame(flush);
  }, [flush]);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  return { ref, pos, handleMove, handleLeave };
};

// Swap these two with the address / link you want public. Kept as constants so
// they're easy to find and change without touching layout.
const EMAIL = 'guy.boxall1@gmail.com';
const LINKEDIN_URL = 'https://www.linkedin.com/in/guyboxall/';
const LINKEDIN_LABEL = 'linkedin.com/in/guyboxall';

// Capabilities are deliberately one-liners now — they describe WHAT I deliver.
// The "how" and the proof live in the case studies below; bloating the capability
// bodies just made the two sections read as the same content twice.
const CAPABILITIES = [
  {
    icon: 'cube',
    title: 'Real-time interactive 3D engineering',
    body:
      'Bespoke graphics, VFX and physics for galleries, brand experiences and permanent installs — built to run unattended for years.',
    tools: ['Unreal Engine', 'Unity', 'TouchDesigner', 'Custom shaders', 'Niagara / VFX'],
  },
  {
    icon: 'network',
    title: 'Applied ML for production',
    body:
      'Fine-tuned neural networks for tracking, perception and bespoke detection — trained, optimised and shipped on edge hardware myself.',
    tools: ['PyTorch', 'ONNX', 'Training pipelines', 'Depth sensors', 'Edge deployment'],
  },
  {
    icon: 'flow',
    title: 'AI-integrated production pipelines',
    body:
      'Generative and agentic AI woven into iteration, asset workflows and operations. Practical deployment, not demos.',
    tools: ['LLM APIs', 'Open-weight models', 'ComfyUI', 'Custom orchestration'],
  },
  {
    icon: 'lighting',
    title: 'Visual direction & creative delivery',
    body:
      'Lighting, shading and look-dev driven from inside the engine — artistic intent that survives all the way to the installed work.',
    tools: ['Unreal Engine', 'HLSL / shaders', 'Lighting & look-dev', 'Niagara / VFX'],
  },
  {
    icon: 'install',
    title: 'On-site installation & production runs',
    body:
      'Site survey, calibration, hardware integration and remote-monitored uptime — from one-offs through medium-volume product runs.',
    tools: ['Calibration tooling', 'Remote diagnostics', 'Hardware integration', 'Production tooling'],
  },
  {
    icon: 'team',
    title: 'Technical leadership',
    body:
      'Multidisciplinary delivery alongside electronics, manufacturing, DevOps and QA — so installs ship on schedule and stay shipped.',
    tools: null,
  },
];

const CASE_STUDIES = [
  {
    n: '01',
    title: 'Replacing a deprecated commercial CV product with a custom edge-deployed neural network',
    paragraphs: [
      "A commercial computer-vision product central to the production pipeline was being deprecated. Off-the-shelf replacements didn't cover the bespoke class set, weren't viable on the embedded hardware, and were trained on RGB where the system needed depth.",
      'I captured my own depth dataset on-site, labelled the bespoke class set, then retrained the head of a pre-trained network on it — owning the full pipeline: data, weights, learning-rate schedules, edge-deployment optimisation.',
      'Now in production across permanent installations globally — fewer external dependencies, tighter performance envelope, rest of the system left intact.',
    ],
    visual: 'layers',
  },
  {
    n: '02',
    title: 'Real-time skeletal tracking trained on a custom, on-site depth dataset',
    paragraphs: [
      "An installation needed real-time skeletal tracking under lighting and occlusion conditions where every off-the-shelf solution failed. The site couldn't change to suit the model — the model had to suit the site.",
      'I captured a custom depth dataset on-site under exact production conditions, labelled it myself, and fine-tuned a skeletal-tracking network on that data. Full pipeline ownership from capture through training.',
      'Robust real-time tracking running on commodity depth hardware, deployed in a permanent install where every off-the-shelf alternative had already failed.',
    ],
    visual: 'skeleton',
  },
  {
    n: '03',
    title: 'Shipping real-time 3D into permanent installations — homes, galleries, public spaces, museums',
    paragraphs: [
      'Permanent installs have a delivery problem most engineering teams never face: no patch cycle, no second build, and years of unattended runtime in environments the technical team never visits again.',
      "I've led the engineering on multiple permanent installs end to end — concept through remote-monitored production — designing for resilience, content updateability and graceful failure modes.",
      'The work runs in private homes, galleries, public spaces and museums internationally.',
    ],
    visual: 'lifecycle',
  },
  {
    n: '04',
    title: 'Real-time visual direction and look-dev in Unreal Engine 5',
    paragraphs: [
      'Large-scale real-time installs carry an unforgiving visual bar. Wrap-around, multi-display, viewer position changing — every frame has to hold up under variable lighting and interaction, with no offline render to fall back on.',
      'I lead the visual side end to end in Unreal Engine 5: lighting, shading, look-dev and the interactive states that drive them. The standard is held frame by frame — no ugly second, ever.',
      'The visual the viewer experiences — across galleries, museums, public spaces and private collections — is one I personally art-direct and sign off on.',
    ],
    visual: 'lighting',
  },
  {
    n: '05',
    title: 'Tooling that turns any physical surface into an interactive touchscreen — at any scale',
    paragraphs: [
      "Installs frequently need surfaces that don't ship as touchscreens — walls, floors, sculptures, projected canvases — to behave like ones. Off-the-shelf options don't scale, don't generalise across sensors, and don't survive long-running production.",
      'I built a framework that combines depth sensing, neural-network-backed perception and engine-side interaction logic to turn arbitrary physical surfaces, at any scale, into responsive interactive touchscreens.',
      'It compounds: tools from one install become primitives for the next, the team builds faster, and the interaction quality keeps climbing.',
    ],
    visual: 'touchsurface',
  },
];

// Track cursor position as CSS vars on the section so a halo gradient can
// follow it. Cheap — no rAF needed for a soft, smoothed gradient.
const handleSpotlight = (e) => {
  const rect = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--mx', `${e.clientX - rect.left}px`);
  e.currentTarget.style.setProperty('--my', `${e.clientY - rect.top}px`);
};

const CubeIcon = () => (
  <svg viewBox="0 0 32 32" className="cap-icon" aria-hidden="true">
    <path
      d="M16 4 L27 10 L27 22 L16 28 L5 22 L5 10 Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
    <path d="M5 10 L16 16 L27 10" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M16 16 L16 28" fill="none" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

const NetworkIcon = () => (
  <svg viewBox="0 0 32 32" className="cap-icon" aria-hidden="true">
    {[6, 16, 26].map((y) => (
      <circle key={`l${y}`} cx="6" cy={y} r="2" fill="currentColor" />
    ))}
    {[10, 22].map((y) => (
      <circle key={`m${y}`} cx="16" cy={y} r="2" fill="currentColor" />
    ))}
    <circle cx="26" cy="16" r="2" fill="currentColor" />
    {[6, 16, 26].map((y1) => (
      [10, 22].map((y2) => (
        <line key={`a${y1}-${y2}`} x1="8" y1={y1} x2="14" y2={y2} stroke="currentColor" strokeWidth="0.9" opacity="0.5" />
      ))
    ))}
    {[10, 22].map((y1) => (
      <line key={`b${y1}`} x1="18" y1={y1} x2="24" y2="16" stroke="currentColor" strokeWidth="0.9" opacity="0.5" />
    ))}
  </svg>
);

const FlowIcon = () => (
  <svg viewBox="0 0 32 32" className="cap-icon" aria-hidden="true">
    <rect x="3" y="6" width="9" height="6" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <rect x="3" y="20" width="9" height="6" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <rect x="20" y="13" width="9" height="6" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <path d="M12 9 C 16 9, 16 16, 20 16" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <path d="M12 23 C 16 23, 16 16, 20 16" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <path d="M19 14.5 L21 16 L19 17.5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
  </svg>
);

const TeamIcon = () => (
  <svg viewBox="0 0 32 32" className="cap-icon" aria-hidden="true">
    <circle cx="16" cy="9" r="3" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <circle cx="6" cy="22" r="3" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <circle cx="16" cy="22" r="3" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <circle cx="26" cy="22" r="3" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <line x1="16" y1="12" x2="16" y2="19" stroke="currentColor" strokeWidth="1.2" />
    <path d="M16 14 L6 19" stroke="currentColor" strokeWidth="1.2" />
    <path d="M16 14 L26 19" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

const LightingIcon = () => (
  <svg viewBox="0 0 32 32" className="cap-icon" aria-hidden="true">
    <circle cx="16" cy="16" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <g stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
      <line x1="16" y1="3" x2="16" y2="7" />
      <line x1="16" y1="25" x2="16" y2="29" />
      <line x1="3" y1="16" x2="7" y2="16" />
      <line x1="25" y1="16" x2="29" y2="16" />
      <line x1="6.5" y1="6.5" x2="9.4" y2="9.4" />
      <line x1="22.6" y1="22.6" x2="25.5" y2="25.5" />
      <line x1="6.5" y1="25.5" x2="9.4" y2="22.6" />
      <line x1="22.6" y1="9.4" x2="25.5" y2="6.5" />
    </g>
  </svg>
);

const InstallIcon = () => (
  <svg viewBox="0 0 32 32" className="cap-icon" aria-hidden="true">
    <rect x="4" y="7" width="24" height="15" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <line x1="11" y1="22" x2="11" y2="26" stroke="currentColor" strokeWidth="1.2" />
    <line x1="21" y1="22" x2="21" y2="26" stroke="currentColor" strokeWidth="1.2" />
    <line x1="9" y1="26" x2="23" y2="26" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M9 14 L13 18 L22 11" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" strokeLinecap="round" />
  </svg>
);

const renderIcon = (key) => {
  if (key === 'cube') return <CubeIcon />;
  if (key === 'network') return <NetworkIcon />;
  if (key === 'flow') return <FlowIcon />;
  if (key === 'team') return <TeamIcon />;
  if (key === 'lighting') return <LightingIcon />;
  if (key === 'install') return <InstallIcon />;
  return null;
};

const LayersVisual = () => {
  const { ref, pos, handleMove, handleLeave } = useSvgMouse();
  // Each layer i sits at x = 22 + i*56, width 42 → centre = x + 21.
  const layerCenter = (i) => 22 + i * 56 + 21;
  const activeLayer = pos.active
    ? [0, 1, 2, 3, 4].reduce(
        (best, i) =>
          Math.abs(pos.x - layerCenter(i)) < Math.abs(pos.x - layerCenter(best)) ? i : best,
        0,
      )
    : -1;
  return (
    <svg
      ref={ref}
      viewBox="0 0 320 200"
      className="cs-svg cs-svg-interactive"
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      aria-hidden="true"
    >
      <rect x="0" y="0" width="320" height="200" fill="rgba(0,0,0,0)" pointerEvents="all" />
      <text x="20" y="32" fontSize="9" letterSpacing="2" fill="rgba(17,17,17,0.42)">PRE-TRAINED BACKBONE</text>
      <text x="190" y="180" fontSize="9" letterSpacing="2" fill="#d4753f">RETRAINED HEAD</text>
      {[0, 1, 2, 3, 4].map((i) => {
        const isHead = i >= 3;
        const x = 22 + i * 56;
        const isActive = i === activeLayer;
        const baseY = isHead ? 60 : 70;
        const baseH = isHead ? 80 : 60;
        const liftY = isActive ? -4 : 0;
        return (
          <g key={i} style={{ transition: 'transform 0.25s cubic-bezier(.4,.1,.2,1)' }} transform={`translate(0 ${liftY})`}>
            <rect
              x={x}
              y={baseY}
              width="42"
              height={baseH}
              rx="3"
              fill={isActive ? 'rgba(212,117,63,0.18)' : isHead ? 'rgba(212,117,63,0.10)' : 'rgba(17,17,17,0.04)'}
              stroke={isActive || isHead ? '#d4753f' : 'rgba(17,17,17,0.22)'}
              strokeWidth={isActive ? 1.6 : 1}
              style={{ transition: 'fill 0.25s ease, stroke-width 0.25s ease' }}
            />
            {i < 4 && (
              <line
                x1={x + 42}
                y1="100"
                x2={x + 56}
                y2="100"
                stroke={
                  pos.active && pos.x > x + 42 && pos.x < x + 56 + 4
                    ? '#d4753f'
                    : i >= 2
                      ? '#d4753f'
                      : 'rgba(17,17,17,0.30)'
                }
                strokeWidth={pos.active && pos.x > x + 42 && pos.x < x + 56 + 4 ? 1.8 : 1}
                style={{ transition: 'stroke 0.2s ease, stroke-width 0.2s ease' }}
              />
            )}
          </g>
        );
      })}
    </svg>
  );
};

const SkeletonVisual = () => {
  const { ref, pos, handleMove, handleLeave } = useSvgMouse();
  // Right arm is animated separately so it can wave when the cursor enters.
  // The remaining joints/lines belong to the body.
  const bodyLines = [
    [160, 50, 160, 120],          // spine
    [120, 65, 200, 65],            // shoulders
    [120, 65, 100, 110], [100, 110, 105, 155],  // left arm
    [135, 120, 185, 120],          // hips
    [135, 120, 130, 175], [185, 120, 190, 175],  // legs
  ];
  const bodyJoints = [
    [160, 50], [120, 65], [200, 65], [100, 110],
    [105, 155], [160, 120], [135, 120], [185, 120],
    [130, 175], [190, 175],
  ];
  const dx = pos.active ? Math.max(-8, Math.min(8, (pos.x - 160) * 0.05)) : 0;
  const dy = pos.active ? Math.max(-5, Math.min(5, (pos.y - 105) * 0.03)) : 0;
  return (
    <svg
      ref={ref}
      viewBox="0 0 320 200"
      className="cs-svg cs-svg-interactive"
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      aria-hidden="true"
    >
      <rect x="0" y="0" width="320" height="200" fill="rgba(0,0,0,0)" pointerEvents="all" />
      <ellipse cx="160" cy="105" rx="125" ry="80" fill="none" stroke="rgba(212,117,63,0.10)" strokeDasharray="2 5" />
      <ellipse cx="160" cy="105" rx="90" ry="58" fill="none" stroke="rgba(212,117,63,0.14)" strokeDasharray="2 5" />
      {pos.active && (
        <line
          x1="160"
          y1="105"
          x2={Math.max(40, Math.min(280, pos.x))}
          y2={Math.max(30, Math.min(180, pos.y))}
          stroke="rgba(212,117,63,0.45)"
          strokeWidth="0.8"
          strokeDasharray="3 3"
        />
      )}
      <g
        transform={`translate(${dx} ${dy})`}
        style={{ transition: 'transform 0.4s cubic-bezier(.4,.1,.2,1)' }}
      >
        <circle cx="160" cy="40" r="9" fill="none" stroke="#d4753f" strokeWidth="1.5" />
        {bodyLines.map(([x1, y1, x2, y2], i) => (
          <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(17,17,17,0.45)" strokeWidth="1" />
        ))}
        {bodyJoints.map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="3" fill="#d4753f" />
        ))}
        <g className={'sk-arm' + (pos.active ? ' is-waving' : '')}>
          <line x1="200" y1="65" x2="220" y2="110" stroke="rgba(17,17,17,0.45)" strokeWidth="1" />
          <line x1="220" y1="110" x2="215" y2="155" stroke="rgba(17,17,17,0.45)" strokeWidth="1" />
          <circle cx="220" cy="110" r="3" fill="#d4753f" />
          <circle cx="215" cy="155" r="3" fill="#d4753f" />
        </g>
      </g>
    </svg>
  );
};

const LifecycleVisual = () => {
  const { ref, pos, handleMove, handleLeave } = useSvgMouse();
  const playheadX = pos.active ? Math.max(30, Math.min(290, pos.x)) : null;
  const isNear = (cx, range = 18) =>
    pos.active && Math.abs(pos.x - cx) < range;
  return (
    <svg
      ref={ref}
      viewBox="0 0 320 200"
      className="cs-svg cs-svg-interactive"
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      aria-hidden="true"
    >
      <rect x="0" y="0" width="320" height="200" fill="rgba(0,0,0,0)" pointerEvents="all" />
      <line x1="30" y1="100" x2="290" y2="100" stroke="rgba(17,17,17,0.20)" strokeWidth="1" />
      {playheadX !== null && (
        <line
          x1={playheadX}
          y1="44"
          x2={playheadX}
          y2="156"
          stroke="rgba(212,117,63,0.55)"
          strokeWidth="0.8"
          strokeDasharray="3 4"
        />
      )}
      <circle
        cx="40"
        cy="100"
        r={isNear(40) ? 8 : 6}
        fill="#fff"
        stroke="#d4753f"
        strokeWidth={isNear(40) ? 1.8 : 1.5}
        style={{ transition: 'r 0.2s ease, stroke-width 0.2s ease' }}
      />
      <text x="40" y="128" fontSize="9" letterSpacing="2" fill={isNear(40) ? '#d4753f' : 'rgba(17,17,17,0.5)'} textAnchor="middle">CONCEPT</text>
      <circle
        cx="100"
        cy="100"
        r={isNear(100) ? 8 : 6}
        fill="#fff"
        stroke="#d4753f"
        strokeWidth={isNear(100) ? 1.8 : 1.5}
        style={{ transition: 'r 0.2s ease, stroke-width 0.2s ease' }}
      />
      <text x="100" y="128" fontSize="9" letterSpacing="2" fill={isNear(100) ? '#d4753f' : 'rgba(17,17,17,0.5)'} textAnchor="middle">BUILD</text>
      <circle
        cx="160"
        cy="100"
        r={isNear(160) ? 9 : 7}
        fill="#d4753f"
        style={{ transition: 'r 0.2s ease' }}
      />
      <text x="160" y="128" fontSize="9" letterSpacing="2" fill={isNear(160) ? '#d4753f' : 'rgba(17,17,17,0.5)'} textAnchor="middle">SHIP</text>
      <rect
        x="180"
        y={isNear(230, 60) ? 92 : 94}
        width="100"
        height={isNear(230, 60) ? 16 : 12}
        rx="6"
        fill={isNear(230, 60) ? 'rgba(212,117,63,0.20)' : 'rgba(212,117,63,0.10)'}
        stroke="#d4753f"
        strokeWidth="1"
        style={{ transition: 'all 0.25s ease' }}
      />
      <text x="230" y="128" fontSize="9" letterSpacing="2" fill="#d4753f" textAnchor="middle">YEARS UNATTENDED</text>
    </svg>
  );
};

const LightingVisual = () => {
  const { ref, pos, handleMove, handleLeave } = useSvgMouse();
  // Light follows the cursor, clamped inside the canvas bounds (40..280, 40..160).
  const lightX = pos.active ? Math.max(50, Math.min(270, pos.x)) : 58;
  const lightY = pos.active ? Math.max(48, Math.min(152, pos.y)) : 58;
  return (
    <svg
      ref={ref}
      viewBox="0 0 320 200"
      className="cs-svg cs-svg-interactive"
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      aria-hidden="true"
    >
      <defs>
        <radialGradient
          id="lightGrad"
          gradientUnits="userSpaceOnUse"
          cx={lightX}
          cy={lightY}
          r="190"
        >
          <stop offset="0%" stopColor="rgba(212,117,63,0.42)" />
          <stop offset="50%" stopColor="rgba(212,117,63,0.08)" />
          <stop offset="100%" stopColor="rgba(212,117,63,0)" />
        </radialGradient>
        <clipPath id="canvasClip">
          <rect x="40" y="40" width="240" height="120" rx="3" />
        </clipPath>
      </defs>
      <rect x="0" y="0" width="320" height="200" fill="rgba(0,0,0,0)" pointerEvents="all" />
      <rect x="40" y="40" width="240" height="120" rx="3" fill="rgba(17,17,17,0.04)" stroke="rgba(17,17,17,0.22)" strokeWidth="1" />
      <g clipPath="url(#canvasClip)">
        <rect x="40" y="40" width="240" height="120" fill="url(#lightGrad)" />
        {[0, 1, 2, 3, 4].map((i) => {
          const len = 90;
          const angle = 18 + i * 16;
          const rad = (angle * Math.PI) / 180;
          return (
            <line
              key={i}
              x1={lightX}
              y1={lightY}
              x2={lightX + Math.cos(rad) * len}
              y2={lightY + Math.sin(rad) * len}
              stroke="rgba(212,117,63,0.32)"
              strokeWidth="0.7"
              strokeDasharray="2 4"
              style={{ transition: 'x1 0.18s ease, y1 0.18s ease, x2 0.18s ease, y2 0.18s ease' }}
            />
          );
        })}
      </g>
      <circle
        cx={lightX}
        cy={lightY}
        r="5"
        fill="#d4753f"
        style={{ transition: 'cx 0.18s cubic-bezier(.4,.1,.2,1), cy 0.18s cubic-bezier(.4,.1,.2,1)' }}
      />
      <text x="40" y="32" fontSize="9" letterSpacing="2" fill="rgba(17,17,17,0.45)">CANVAS</text>
      <text x="280" y="180" fontSize="9" letterSpacing="2" fill="#d4753f" textAnchor="end">LIGHTING · SHADING</text>
    </svg>
  );
};

const TouchSurfaceVisual = () => {
  const { ref, pos, handleMove, handleLeave } = useSvgMouse();
  const staticPoints = [
    [110, 130],
    [180, 105],
    [225, 145],
    [145, 75],
  ];
  // Static touch points subtly intensify when the cursor is close.
  const intensity = (cx, cy) => {
    if (!pos.active) return 0;
    const dx = pos.x - cx;
    const dy = pos.y - cy;
    const d = Math.sqrt(dx * dx + dy * dy);
    return Math.max(0, 1 - d / 80);
  };
  return (
    <svg
      ref={ref}
      viewBox="0 0 320 200"
      className="cs-svg cs-svg-interactive"
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      aria-hidden="true"
    >
      <rect x="0" y="0" width="320" height="200" fill="rgba(0,0,0,0)" pointerEvents="all" />
      <polygon
        points="48,170 280,170 252,40 76,40"
        fill="rgba(17,17,17,0.04)"
        stroke="rgba(17,17,17,0.22)"
        strokeWidth="1"
      />
      {staticPoints.map(([cx, cy], i) => {
        const k = intensity(cx, cy);
        return (
          <g key={i} style={{ transition: 'opacity 0.2s ease' }}>
            <circle cx={cx} cy={cy} r={16 + k * 4} fill="none" stroke={`rgba(212,117,63,${0.16 + k * 0.18})`} strokeWidth="0.6" />
            <circle cx={cx} cy={cy} r={11 + k * 3} fill="none" stroke={`rgba(212,117,63,${0.30 + k * 0.20})`} strokeWidth="0.7" />
            <circle cx={cx} cy={cy} r={6 + k * 2} fill="none" stroke={`rgba(212,117,63,${0.50 + k * 0.20})`} strokeWidth="0.8" />
            <circle cx={cx} cy={cy} r={2.5 + k * 1.2} fill="#d4753f" />
          </g>
        );
      })}
      {pos.active && (
        <g style={{ pointerEvents: 'none' }}>
          <circle cx={pos.x} cy={pos.y} r="22" fill="none" stroke="rgba(212,117,63,0.18)" strokeWidth="0.6" />
          <circle cx={pos.x} cy={pos.y} r="14" fill="none" stroke="rgba(212,117,63,0.32)" strokeWidth="0.7" />
          <circle cx={pos.x} cy={pos.y} r="7" fill="none" stroke="rgba(212,117,63,0.55)" strokeWidth="0.9" />
          <circle cx={pos.x} cy={pos.y} r="3" fill="#d4753f" />
        </g>
      )}
      <text x="56" y="32" fontSize="9" letterSpacing="2" fill="rgba(17,17,17,0.45)">SURFACE</text>
      <text x="270" y="190" fontSize="9" letterSpacing="2" fill="#d4753f" textAnchor="end">TOUCH · INTERACTION</text>
    </svg>
  );
};

const renderVisual = (key) => {
  if (key === 'layers') return <LayersVisual />;
  if (key === 'skeleton') return <SkeletonVisual />;
  if (key === 'lifecycle') return <LifecycleVisual />;
  if (key === 'lighting') return <LightingVisual />;
  if (key === 'touchsurface') return <TouchSurfaceVisual />;
  return null;
};

const Skills = () => {
  useEffect(() => {
    const els = document.querySelectorAll('.sk-reveal');
    if (!els.length) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '40px' },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <main className="sk-page" aria-label="About Guy Boxall — Senior Creative Technologist">
      <section className="sk-hero" onMouseMove={handleSpotlight}>
        <div className="sk-hero-inner">
          <h1 className="sk-hero-title">Senior Interactive Creative Technologist</h1>
          <p className="sk-hero-subhead">
            Real-time interactive 3D, applied neural networks, and the visual craft that makes them beautiful — for high-end creative installations.
          </p>
          <p className="sk-hero-lede">
            A decade shipping bespoke interactive installations worldwide.
            I lead delivery, build the engineering team, and ship the code — with an eye on the visual that makes the work land.
          </p>
          <p className="sk-hero-manifesto">
            Most engineers ship neural networks that <em>work</em>. Most artists ship visuals that <em>look good</em>. The interesting work lives where engineering and art stop being separate jobs.
          </p>
        </div>
      </section>

      <section className="sk-section" aria-labelledby="cap-h">
        <h2 id="cap-h" className="sk-section-title">Capabilities</h2>
        <ul className="sk-cap-list">
          {CAPABILITIES.map((c, i) => (
            <li
              key={c.title}
              className="sk-cap-card sk-reveal"
              onMouseMove={handleSpotlight}
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <div className="sk-cap-head">
                <span className="sk-cap-icon" aria-hidden="true">{renderIcon(c.icon)}</span>
                <h3 className="sk-cap-title">{c.title}</h3>
              </div>
              <p className="sk-cap-body">{c.body}</p>
              {c.tools && (
                <div className="sk-cap-tools">
                  <span className="sk-cap-tools-label">Tools</span>
                  <div className="sk-chips">
                    {c.tools.map((t) => (
                      <span key={t} className="sk-chip">{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="sk-section sk-cases" aria-labelledby="cs-h">
        <h2 id="cs-h" className="sk-section-title">Case studies</h2>
        <ol className="sk-cs-list">
          {CASE_STUDIES.map((cs) => (
            <li key={cs.n} className="sk-cs sk-reveal">
              <figure className="sk-cs-visual">{renderVisual(cs.visual)}</figure>
              <div className="sk-cs-text">
                <span className="sk-cs-num">Case {cs.n}</span>
                <h3 className="sk-cs-title">{cs.title}</h3>
                {cs.paragraphs.map((p, idx) => (
                  <p key={idx} className={`sk-cs-p${idx === cs.paragraphs.length - 1 ? ' sk-cs-p-out' : ''}`}>
                    {p}
                  </p>
                ))}
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="sk-section sk-about" aria-labelledby="ab-h">
        <h2 id="ab-h" className="sk-section-title">About</h2>
        <div className="sk-about-body">
          <p>
            Based in London / Surrey, leading the engineering side at a creative-tech studio.
            I build the visual systems, train the networks, and grow the team that ships them.
            I don&apos;t make systems that are just functional — I make them beautiful pieces
            people stand in front of.
          </p>
          <p>
            I&apos;m most interested in work where the engineering problem is real and the
            deployment context isn&apos;t a slide. The interesting part is where research-grade
            techniques have to survive contact with production: the edge hardware, the gallery
            floor, and the viewer standing in front of the piece.
          </p>
        </div>
      </section>

      <section className="sk-section sk-contact" aria-labelledby="co-h">
        <h2 id="co-h" className="sk-section-title">Contact</h2>
        <ul className="sk-contact-list">
          <li>
            <span className="sk-contact-key">Email</span>
            <a className="sk-contact-link" href={`mailto:${EMAIL}`}>{EMAIL}</a>
          </li>
          <li>
            <span className="sk-contact-key">LinkedIn</span>
            <a
              className="sk-contact-link"
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {LINKEDIN_LABEL}
            </a>
          </li>
        </ul>
      </section>
    </main>
  );
};

export default Skills;
